import React, { useState, useEffect } from 'react';
import {
	Typography,
	Divider,
	Grid,
	TextField,
	FormControl,
	FormControlLabel,
	Radio,
	RadioGroup,
	FormLabel,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	Paper,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useSelector, useDispatch } from 'react-redux';
import {
	bankAction,
	houseCategoryAction,
	houseRangeAction,
	districtAction,
	loanAction,
} from '../../redux/actions/applicationAction';
import LoanCalculation from './LoanCalculation';

const LoanDetails = ({
	salarynet,
	spouseSalarynet,
	handleHouseRange,
	handleBankProvider,
	handleBankQuestion,
	handleBankProvider2,
	handleDistrict,
	handleDistrict2,
	handleHouseCategory,
	handleHouseQuestion,
	handleHouseCategory2,
	handleMeetDown,
	handleInsured,
	handleHouseholdDisability,
	handleOwnResidentialUnit,
	houseRangeId,
	bankProviderId1,
	bankQuestion,
	bankProviderId2,
	district,
	district2,
	houseCategoryId1,
	houseQuestion,
	houseCategoryId2,
	meetDownCapacity,
	insured,
	householdDisability,
	ownResidentialUnit,
	fieldError,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const categories = useSelector(state => state.houseCategory);
	const ranges = useSelector(state => state.houseRange);
	const houseRanges = ranges.data.ranges;
	const bankData = useSelector(state => state.banks);
	const banks = bankData.data.Banks;
	const districtData = useSelector(state => state.district);
	const loan = useSelector(state => state.loan);

	const meetDownFigure = `(Rwf ${Math.round(
		loan.data.breakdown &&
			loan.data.breakdown.propertyValueLimit10PercentMeetDown
	).toLocaleString()})`;
	let districts;
	if (districtData.data.provinces !== undefined) {
		const provinces = districtData.data.provinces;
		districts = provinces.map(p => p.districts.map(d => d.name)).flat();
	}

	const houseCategories = categories.data.categories;

	const totalIncome = Number(salarynet) + Number(spouseSalarynet);

	useEffect(() => {
		dispatch(houseCategoryAction());
		dispatch(houseRangeAction({ totalIncome }));
		dispatch(bankAction());
		dispatch(districtAction());
		dispatch(loanAction({ totalIncome }));
	}, [dispatch]);

	return (
		<div>
			<Typography variant='h6' color='initial'>
				{t('loan-details')}
			</Typography>
			<Divider />
			<Grid container>
				<Grid item xs={12} sm={12} md={5}>
					<LoanCalculation loan={loan} />
					{houseRanges !== undefined && (
						<div
							className={
								fieldError && fieldError === t('houseRange-required')
									? 'overall-error'
									: 'overall-error-none'
							}
						>
							<Autocomplete
								size='small'
								id='houseRangeId'
								options={houseRanges}
								getOptionLabel={option =>
									option.name === undefined ? '' : option.name
								}
								onChange={handleHouseRange}
								value={houseRangeId}
								getOptionSelected={(option, value) => value === houseRangeId}
								renderInput={params => (
									<TextField
										margin='normal'
										{...params}
										label={t('house-range')}
										variant='outlined'
									/>
								)}
							/>
						</div>
					)}
					<FormControl component='fieldset' margin='normal'>
						<FormLabel component='legend'>
							{t('capacity-question', { meetDownFigure })}
						</FormLabel>
						<div
							className={
								fieldError && fieldError === t('meetDownCapacity-required')
									? 'overall-error'
									: 'overall-error-none'
							}
						>
							<RadioGroup name='meetDownCapacity' onChange={handleMeetDown}>
								<Grid container>
									<Grid item>
										<FormControlLabel
											value='Yes'
											control={<Radio color='default' />}
											label={t('guardian-yes')}
											checked={meetDownCapacity === 'Yes'}
										/>
									</Grid>
									<Grid item>
										<FormControlLabel
											value='No'
											control={<Radio color='default' />}
											label={t('guardian-no')}
											checked={meetDownCapacity === 'No'}
										/>
									</Grid>
								</Grid>
							</RadioGroup>
						</div>
					</FormControl>
					{meetDownCapacity === 'No' && (
						<FormControl component='fieldset' margin='normal'>
							<FormLabel component='legend'>
								{t('insurance-question')}
							</FormLabel>
							<div
								className={
									fieldError && fieldError === t('insured-required')
										? 'overall-error'
										: 'overall-error-none'
								}
							>
								<RadioGroup name='insured' onChange={handleInsured}>
									<Grid container>
										<Grid item>
											<FormControlLabel
												value='Yes'
												control={
													<Radio color='default' disabled={!meetDownCapacity} />
												}
												label={t('guardian-yes')}
												checked={insured === 'Yes'}
											/>
										</Grid>
										<Grid item>
											<FormControlLabel
												value='No'
												control={
													<Radio color='default' disabled={!meetDownCapacity} />
												}
												label={t('guardian-no')}
												checked={insured === 'No'}
											/>
										</Grid>
									</Grid>
								</RadioGroup>
							</div>
						</FormControl>
					)}

					{houseCategories !== undefined && (
						<>
							<div
								className={
									fieldError && fieldError === t('houseCategory-required')
										? 'overall-error'
										: 'overall-error-none'
								}
							>
								<Autocomplete
									size='small'
									id='houseCategoryId1'
									options={houseCategories}
									getOptionLabel={option =>
										option.name === undefined ? '' : option.name
									}
									onChange={handleHouseCategory}
									value={houseCategoryId1}
									getOptionSelected={(option, value) =>
										value === houseCategoryId1
									}
									renderInput={params => (
										<TextField
											margin='normal'
											{...params}
											label={t('house-category')}
											variant='outlined'
										/>
									)}
								/>
							</div>
							<Autocomplete
								size='small'
								id='houseCategoryId2'
								options={houseCategories}
								getOptionLabel={option =>
									option.name === undefined ? '' : option.name
								}
								onChange={handleHouseCategory2}
								value={houseCategoryId2}
								getOptionSelected={(option, value) =>
									value === houseCategoryId2
								}
								getOptionDisabled={option => option === houseCategoryId1}
								renderInput={params => (
									<TextField
										margin='normal'
										{...params}
										label={t('house-category2')}
										variant='outlined'
									/>
								)}
							/>
						</>
					)}

					{districts !== undefined && (
						<>
							<div
								className={
									fieldError && fieldError === t('district-required')
										? 'overall-error'
										: 'overall-error-none'
								}
							>
								<Autocomplete
									size='small'
									id='district'
									options={districts}
									getOptionLabel={option => option}
									onChange={handleDistrict}
									value={district}
									getOptionSelected={(option, value) => value === district}
									renderInput={params => (
										<TextField
											margin='normal'
											{...params}
											label={t('district')}
											variant='outlined'
										/>
									)}
								/>
							</div>
							<Autocomplete
								size='small'
								id='district2'
								options={districts}
								getOptionLabel={option => option}
								onChange={handleDistrict2}
								value={district2}
								getOptionSelected={(option, value) => value === district2}
								getOptionDisabled={option => option === district}
								renderInput={params => (
									<TextField
										margin='normal'
										{...params}
										label={t('second-district')}
										variant='outlined'
									/>
								)}
							/>
						</>
					)}
					{banks !== undefined && (
						<div
							className={
								fieldError && fieldError === t('bank-required')
									? 'overall-error'
									: 'overall-error-none'
							}
						>
							<Autocomplete
								size='small'
								id='bankProviderId1'
								options={banks}
								getOptionLabel={option =>
									option.name === undefined ? '' : option.name
								}
								onChange={handleBankProvider}
								value={bankProviderId1}
								getOptionSelected={(option, value) => value === bankProviderId1}
								renderInput={params => (
									<TextField
										margin='normal'
										{...params}
										label={t('loan-provider')}
										variant='outlined'
									/>
								)}
							/>
						</div>
					)}
				</Grid>
			</Grid>
		</div>
	);
};

export default LoanDetails;
